import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { v4 as uuid } from 'uuid';
import Header from './components/shared/header';
import Notification from './components/shared/notification';
import { AppRoutes } from './navigation/app-routes';
import { updateUser } from './store/auth/actions';
import {
  getCookie,
  removeCookie,
  setCookie,
  setCookieForCrossOrigin,
  USER_COOKIE,
  DEVICE_ID_COOKIE,
  clearLegacyBareCookies
} from './utils/cookie-service';
import Loader from './components/shared/loader';
import routes from './constants/routes';
import { withRouter } from './utils/withRouter';
import JobComments from './components/candidate/job-comments';
import RestrictedAccess from './components/RestrictedAccess';
import CommanFooter from './components/shared/commanFooter';
import { getIpCountry } from './utils/utility';
import { updateSpinner } from './store/global/actions';
import { fetchProfile } from './store/candidate/actions';
import { SocketProvider } from './contexts/SocketContext';
import { useSessionSync } from './hooks/useSessionSync';

function App(props) {
  /* For Country restriction */
  const [hasAccess, setHasAccess] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Function to fetch user's country
  const fetchUserCountry = async () => {
    updateSpinner(true);
    setIsLoading(true);
    const data = await getIpCountry();

    setHasAccess(data);
    updateSpinner(false);
    setIsLoading(false);
  };

  // VTS-593: sweep orphaned pre-fix bare cookies once per load (non-prod no-op
  // on production). Only ever removes the BARE legacy names, never the active
  // env-scoped cookies, so this is safe regardless of render order.
  useEffect(() => {
    clearLegacyBareCookies();
  }, []);

  let deviceId = getCookie(DEVICE_ID_COOKIE);
  if (!deviceId) {
    deviceId = uuid().toString();
    setCookieForCrossOrigin(DEVICE_ID_COOKIE, deviceId);
  }

  const { navigate, auth, location, fetchProfile } = props;

  useEffect(() => {
    const notRedirectROutes = [
      routes.login,
      routes.register,
      routes.forgotPassword,
      routes.thankYou,
      routes.notFound,
      routes.parseResume
    ];
    if (!notRedirectROutes.includes(location.pathname)) {
      setCookie('redirectPath', location.pathname);
    }
    if (auth.isAuthenticated === false) navigate(routes.login);
    if (auth.isAuthenticated === true) fetchProfile();
  }, [auth.isAuthenticated]);

  useEffect(() => {
    if (
      process.env.REACT_APP_ALLOWED_HOST !== window.location.hostname &&
      location.pathname !== routes.notFound
    ) {
      setTimeout(() => {
        navigate(routes.notFound);
      }, 50);
    } else if (location.pathname !== getCookie('prev-path')) removeCookie('filters');
    const isOnboarding = getCookie('onBoarding');
    if (
      !isOnboarding &&
      auth.isAuthenticated &&
      location.search === '' &&
      (location.pathname === routes.login || location.pathname === routes.register)
    ) {
      navigate(routes.dashboard);
    }
    setCookie('prev-path', location.pathname);
  }, [location.pathname, auth.isAuthenticated]);

  const user = getCookie(USER_COOKIE);
  if (user && !auth.currentUser) {
    const { updateUser } = props;
    updateUser(user);
  }

  // Cross-tab logout detection: if token disappears (logged out from another tab),
  // dispatch logout action to update Redux state
  useSessionSync({
    onLogout: () => {
      const { updateUser } = props;
      updateUser(null);
      // Full page navigation to clear all React state (navigate() leaves stale state)
      window.location.href = '/login/';
    },
    // Only poll when user is authenticated to avoid unnecessary work
    enabled: auth.isAuthenticated === true
  });

  /* For Country restriction */
  useEffect(() => {
    fetchUserCountry();
  }, []);

  if (isLoading) {
    return <Loader />;
  }

  if (!hasAccess) {
    return (
      <div>
        <Header />
        <RestrictedAccess />
        <CommanFooter />
      </div>
    );
  }
  /* End code  */

  return (
    <div>
      <SocketProvider userId={auth?.currentUser?.id} isAuthenticated={auth?.isAuthenticated}>
        <JobComments />
        <Notification />
        <Loader />
        <Header />
        <div style={{ minHeight: '70vh' }}>
          <AppRoutes />
        </div>
        <CommanFooter />
      </SocketProvider>
    </div>
  );
}

export default connect(
  (state) => ({
    selectedCandidate: state.global.selectedCandidate,
    auth: state.auth
  }),
  { updateUser, fetchProfile }
)(withRouter(App));
